import {
  Autocomplete,
  debounce,
  IconButton,
  InputAdornment,
  ListItem,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { ChangeEvent, useCallback, useMemo, useState } from 'react';
import { identity } from 'remeda';
import { DynamicIcon } from 'src/components';
import { SEARCH_DEBOUNCE_DELAY } from 'src/constants/delays';
import { Address } from 'src/graphql';
import { useSearch } from 'src/graphql/entities/search/use-search';
import { ConditionInput, EntityNames } from 'src/graphql/types';
import { SiteCard } from 'src/pages/Conversations/SidePanel/ConversationDetails/ConversationSite/site-card';
import { useScrollPaginateHandler } from 'src/utils/hooks';

interface SiteSelectorProps {
  address: Address | null;
  filters?: ConditionInput[];
  title?: string;
  deletable?: boolean;
  setAddress?: (address: Address | null) => void;
}

export interface ConversationOption {
  id: string | null;
  label: string;
}

export const SiteSelector = ({
  address,
  title,
  deletable,
  filters,
  setAddress,
}: SiteSelectorProps) => {
  const [value, setValue] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const {
    records: addresses,
    loadMore,
    isLoading,
    hasMore,
  } = useSearch({
    entityName: EntityNames.Site,
    searchTerm,
    filter: filters,
  }) ?? {};

  const handleScroll = useScrollPaginateHandler({
    loadMore,
    enabled: hasMore,
    thresholdPx: 200,
  });

  const filteredAddresses = useMemo(
    () =>
      addresses?.map((address) => ({
        label: address.fullAddress,
        id: address.addressId,
      })) || [],
    [addresses],
  );

  const handleOnDelete = useCallback(() => {
    setAddress?.(null);
  }, [setAddress]);

  const onInputChange = debounce(
    ({ target }: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setSearchTerm(target.value);
    },
    SEARCH_DEBOUNCE_DELAY,
  );

  const deleteSelectedAddressControl = useMemo(() => {
    return deletable ? (
      <IconButton size="small" onClick={() => handleOnDelete()}>
        <DynamicIcon name="close" />
      </IconButton>
    ) : null;
  }, [deletable, handleOnDelete]);

  return address ? (
    <Stack gap={1}>
      <Typography
        variant="subtitle2"
        ml={1}
        sx={{ color: (theme) => theme.palette.text.secondary }}
      >
        {title}:
      </Typography>
      <SiteCard viewOnly address={address} controls={deleteSelectedAddressControl} />
    </Stack>
  ) : (
    <Autocomplete
      autoHighlight
      value={value}
      loading={isLoading}
      fullWidth
      onChange={(_, addressToBeSelected) => {
        if (addressToBeSelected) {
          const currentAddress =
            addresses?.find((conversation) => conversation.id === addressToBeSelected.id) ??
            null;
          setAddress?.(currentAddress);
          setValue(null);
        }
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={`Search & Select ${title}`}
          autoFocus
          onChange={onInputChange}
          InputProps={{
            ...params.InputProps,
            style: {
              padding: '0.375rem 1rem',
              borderRadius: '1rem',
            },
            startAdornment: (
              <InputAdornment position="start">
                <DynamicIcon name="search" />
              </InputAdornment>
            ),
            endAdornment: null,
          }}
        />
      )}
      options={filteredAddresses}
      renderOption={(props, option) => (
        <ListItem {...props} key={option.id}>
          <ListItemText primary={option.label} />
        </ListItem>
      )}
      ListboxProps={{
        onScroll: handleScroll,
      }}
      filterOptions={identity}
    />
  );
};
